<template>
  <div class="widget-container" :class="{ 'no-drag': isMobile }">
    <v-card class="widget-card" outlined>
      <div class="widget-header">
        <!-- Drag-Handle als separater Container -->
        <div v-if="!isMobile" class="drag-handle" title="Ziehen, um die Position zu ändern">
          <v-icon size="18" color="grey">mdi-drag</v-icon>
        </div>

        <div class="d-flex align-center flex-grow-1">
          <v-icon
            size="20"
            class="mr-2"
            :color="element.collapsed ? 'grey' : 'primary'"
          >
            {{ getHeaderIcon(element.name) }}
          </v-icon>
          <span class="widget-title">
            {{ getCardTitle(element.name) }}
          </span>
        </div>

        <!-- Action-Buttons mit besserer Gruppierung -->
        <div class="d-flex align-center">
          <template v-if="element.name === 'durchwahl'">
            <v-btn
              icon
              size="small"
              variant="text"
              class="action-button"
              @click="$emit('show-durchwahl-search')"
              title="Durchwahlen durchsuchen"
            >
              <v-icon color="primary">mdi-magnify</v-icon>
            </v-btn>
          </template>

          <template v-else-if="element.name === 'kurztexte'">
            <v-btn
              icon
              size="small"
              variant="text"
              class="action-button"
              @click="$emit('show-kurztext-search')"
              title="Kurztexte durchsuchen"
            >
              <v-icon color="primary">mdi-magnify</v-icon>
            </v-btn>
          </template>

          <template v-else-if="element.name === 'greeting'">
            <v-btn
              icon
              size="small"
              variant="text"
              class="action-button mx-1"
              @click="$emit('show-modelle-search')"
              title="Modelle durchsuchen"
            >
              <v-icon color="primary">mdi-magnify</v-icon>
            </v-btn>
            <v-btn
              icon
              size="small"
              variant="text"
              class="action-button"
              @click="$emit('show-add-modelle')"
              title="Neues Modell hinzufügen"
            >
              <v-icon color="primary">mdi-plus</v-icon>
            </v-btn>
          </template>

          <template v-else-if="element.name === 'notes'">
            <v-btn
              icon
              size="small"
              variant="text"
              class="action-button mx-1"
              @click="$emit('show-notes-search')"
              title="Notizen durchsuchen"
            >
              <v-icon color="primary">mdi-magnify</v-icon>
            </v-btn>
            <v-btn
              icon
              size="small"
              variant="text"
              class="action-button"
              @click="$emit('show-add-note')"
              title="Neue Notiz hinzufügen"
            >
              <v-icon color="primary">mdi-plus</v-icon>
            </v-btn>
          </template>

          <v-btn
            icon
            size="small"
            variant="text"
            class="action-button ml-1"
            @click="$emit('toggle-collapse', element)"
            :title="element.collapsed ? 'Ausklappen' : 'Einklappen'"
          >
            <v-icon color="primary" :class="{ collapsed: element.collapsed }">
              mdi-chevron-down
            </v-icon>
          </v-btn>
        </div>
      </div>

      <!-- Karteninhalt mit verbessertem Animation und Layout -->
      <v-expand-transition>
        <div v-show="!element.collapsed" class="widget-content-wrapper">
          <!-- Modelle-Widget -->
          <template v-if="element.name === 'greeting'">
            <modelle-widget />
          </template>

          <!-- Kalender-Widget -->
          <template v-else-if="element.name === 'calendar'">
            <calendar-widget />
          </template>

          <!-- Uhr und aktive Benutzer Widget -->
          <template v-else-if="element.name === 'clock'">
            <clock-widget />
          </template>

          <!-- Daily Statistiken -->
          <template v-else-if="element.name === 'daily'">
            <daily-stats-widget />
          </template>

          <!-- Monthly Statistiken -->
          <template v-else-if="element.name === 'monthly'">
            <monthly-stats-widget />
          </template>

          <!-- Durchwahl-Widget -->
          <template v-else-if="element.name === 'durchwahl'">
            <durchwahl-widget />
          </template>

          <!-- Kurztexte-Widget -->
          <template v-else-if="element.name === 'kurztexte'">
            <kurztexte-widget />
          </template>

          <!-- Notizen-Widget -->
          <template v-else-if="element.name === 'notes'">
            <notes-widget />
          </template>
        </div>
      </v-expand-transition>
    </v-card>
  </div>
</template>

<script setup>
import { inject } from 'vue';
// Compiler-Makros für Vue, die ESLint noch nicht kennt
 
import ModelleWidget from '../views/widgets/ModelleWidget.vue';
import CalendarWidget from '../views/widgets/CalendarWidget.vue';
import ClockWidget from '../views/widgets/ClockWidget.vue';
import DailyStatsWidget from '../views/widgets/DailyStatsWidget.vue';
import MonthlyStatsWidget from '../views/widgets/MonthlyStatsWidget.vue';
import DurchwahlWidget from '../views/widgets/DurchwahlWidget.vue';
import KurztexteWidget from '../views/widgets/KurztexteWidget.vue';
import NotesWidget from '../views/widgets/NotesWidget.vue';
/* eslint-disable @typescript-eslint/no-unused-vars */
// Props
const props = defineProps({
  element: {
    type: Object,
    required: true
  }
});

// Emits
const emit = defineEmits([
  'toggle-collapse',
  'show-modelle-search',
  'show-add-modelle',
  'show-durchwahl-search',
  'show-kurztext-search',
  'show-notes-search',
  'show-add-note'
]);
 
/* eslint-enable @typescript-eslint/no-unused-vars */
// Inject gemeinsam genutzte Methoden
const getHeaderIcon = inject('getHeaderIcon');
const getCardTitle = inject('getCardTitle');

// Injektion des isMobile-Status
const isMobile = inject('isMobile');
</script>

<style scoped>
/* ======== Widget Card Design (im CalendarView-Stil) ======== */
.widget-card {
  background: rgba(25, 25, 30, 0.85) !important;
  backdrop-filter: blur(15px);
  -webkit-backdrop-filter: blur(15px);
  border-radius: 18px !important;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3) !important;
  border: 1px solid rgba(255, 255, 255, 0.08) !important;
  overflow: hidden;
  transition: box-shadow 0.3s ease, transform 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  cursor: default;
}

.widget-card:hover {
  box-shadow: 0 12px 36px rgba(0, 0, 0, 0.35) !important;
  transform: translateY(-2px);
}

.widget-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background: linear-gradient(to right, rgba(30, 30, 40, 0.9), rgba(25, 25, 35, 0.85));
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  position: relative;
  z-index: 1;
  min-height: 64px;
  border-top-left-radius: 18px;
  border-top-right-radius: 18px;
}

.widget-title {
  font-size: 16px;
  font-weight: 500;
  margin: 0;
  color: rgba(255, 255, 255, 0.9);
  letter-spacing: 0.3px;
  background: linear-gradient(45deg, #ffffff, rgba(255, 255, 255, 0.8));
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}

.widget-content-wrapper {
  padding: 0;
}

.action-button {
  border-radius: 8px !important;
  opacity: 0.8;
  transition: all 0.2s ease;
}

.action-button:hover {
  transform: translateY(-2px);
  opacity: 1;
  background-color: rgba(33, 150, 243, 0.15) !important;
}

/* ======== Animation für Icons ======== */
.collapsed {
  transform: rotate(-90deg);
  transition: transform 0.3s ease;
}

/* ======== Drag & Drop ======== */
.draggable-box {
  cursor: grab;
  margin-bottom: 16px;
}

.draggable-box:active {
  cursor: grabbing;
}

@media (max-width: 960px) {
  .widget-header {
    padding: 14px 16px;
  }
}

@media (max-width: 600px) {
  .widget-card {
    border-radius: 14px !important;
  }

  .widget-header {
    padding: 12px 16px;
    min-height: 56px;
    border-top-left-radius: 14px;
    border-top-right-radius: 14px;
  }

  .widget-title {
    font-size: 15px;
  }
}

/* Neues Styling für den Drag-Handle */
.drag-handle {
  cursor: grab;
  color: rgba(255, 255, 255, 0.4);
  transition: all 0.2s ease;
  padding: 5px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  margin-right: 8px;
  background-color: rgba(255, 255, 255, 0.05);
}

.drag-handle:hover {
  color: rgba(255, 255, 255, 0.9);
  background-color: rgba(255, 255, 255, 0.1);
}

.drag-handle:active {
  cursor: grabbing;
  color: var(--v-primary-base);
  background-color: rgba(255, 255, 255, 0.15);
}

/* Entfernen des Grab-Cursors vom gesamten Widget auf mobilen Geräten */
.no-drag.draggable-box {
  cursor: default;
}

.no-drag .widget-card {
  cursor: default;
}

.no-drag .drag-handle {
  display: none;
}

/* Entfernen des Grab-Cursors von der gesamten Karte */
.widget-container {
  margin-bottom: 16px;
}
</style>
